/**
 * Görev oluştur / düzenle modalı (resmi şema).
 * gorev=null → yeni. gorev dolu → düzenleme.
 *
 * Haftalık/aylık tekrar AYRI alan değil — başlangıç tarihinden türetilir.
 */

import { useEffect, useState } from 'react'
import { X, ClipboardList } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  gorevOlustur, gorevGuncelle, type GorevDoc, type GorevOlusturData,
} from '@/lib/services/gorev-service'
import type { GorevTipi, GorevHedefi, Sube, Pozisyon, KullaniciPersonel } from '@/types'

interface GorevModalProps {
  acik: boolean
  gorev: GorevDoc | null
  subeler: Sube[]
  pozisyonlar: Pozisyon[]
  personeller: KullaniciPersonel[]
  onKapat: () => void
  onKaydedildi: () => void
}

const TIP_SECENEKLERI: { value: GorevTipi; label: string }[] = [
  { value: 'tekSeferlik', label: 'Tek seferlik (belirli bir gün)' },
  { value: 'rutinGunluk', label: 'Her gün' },
  { value: 'rutinHaftalik', label: 'Her hafta (başlangıç gününde tekrarlar)' },
  { value: 'rutinAylik', label: 'Her ay (başlangıç günü sayısında tekrarlar)' },
  { value: 'devamEden', label: 'Devam eden (bitene kadar)' },
]

const HAFTA_ADI = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi']

function tarihIpucu(tipi: GorevTipi, tarih: string): string | null {
  if (!tarih) return null
  const [y, m, d] = tarih.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  if (tipi === 'rutinHaftalik') {
    return `📅 Bu bir ${HAFTA_ADI[dt.getDay()]} — her ${HAFTA_ADI[dt.getDay()]} tekrarlanacak.`
  }
  if (tipi === 'rutinAylik') {
    return `📅 Her ayın ${d}'i tekrarlanacak.`
  }
  return null
}

export function GorevModal({
  acik, gorev, subeler, pozisyonlar, personeller, onKapat, onKaydedildi,
}: GorevModalProps) {
  const { firebaseUser, kullanici } = useAuth()
  const duzenleme = !!gorev

  const [baslik, setBaslik] = useState('')
  const [aciklama, setAciklama] = useState('')
  const [tipi, setTipi] = useState<GorevTipi>('tekSeferlik')
  const [baslangicTarihi, setBaslangicTarihi] = useState('')
  const [bitisTarihi, setBitisTarihi] = useState('')
  const [zamanVar, setZamanVar] = useState(false)
  const [zBas, setZBas] = useState('')
  const [zBit, setZBit] = useState('')
  const [mesaiDisiDahil, setMesaiDisiDahil] = useState(false)
  const [paylasimli, setPaylasimli] = useState(false)
  const [seciliSubeler, setSeciliSubeler] = useState<string[]>([])
  const [seciliPozisyonlar, setSeciliPozisyonlar] = useState<string[]>([])
  const [seciliPersoneller, setSeciliPersoneller] = useState<string[]>([])
  const [fotoZorunlu, setFotoZorunlu] = useState(false)
  const [aciklamaZorunlu, setAciklamaZorunlu] = useState(false)

  const [kaydediliyor, setKaydediliyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  useEffect(() => {
    if (!acik) return
    setHata(null)
    if (gorev) {
      setBaslik(gorev.baslik)
      setAciklama(gorev.aciklama)
      setTipi(gorev.tipi)
      setBaslangicTarihi(gorev.baslangicTarihi as unknown as string)
      setBitisTarihi((gorev.bitisTarihi as unknown as string) ?? '')
      setZamanVar(!!gorev.zamanAraligi)
      setZBas(gorev.zamanAraligi?.baslangic ?? '')
      setZBit(gorev.zamanAraligi?.bitis ?? '')
      setMesaiDisiDahil(!!gorev.mesaiDisiDahil)
      setPaylasimli(!!gorev.paylasimli)
      setSeciliSubeler(gorev.hedef?.subeIdleri ?? [])
      setSeciliPozisyonlar(gorev.hedef?.pozisyonIdleri ?? [])
      setSeciliPersoneller(gorev.hedef?.personelIdleri ?? [])
      setFotoZorunlu(gorev.fotoZorunlu)
      setAciklamaZorunlu(gorev.aciklamaZorunlu)
    } else {
      const bugun = new Date()
      const g = `${bugun.getFullYear()}-${String(bugun.getMonth() + 1).padStart(2, '0')}-${String(bugun.getDate()).padStart(2, '0')}`
      setBaslik('')
      setAciklama('')
      setTipi('tekSeferlik')
      setBaslangicTarihi(g)
      setBitisTarihi('')
      setZamanVar(false)
      setZBas('')
      setZBit('')
      setMesaiDisiDahil(false)
      setPaylasimli(false)
      setSeciliSubeler([])
      setSeciliPozisyonlar([])
      setSeciliPersoneller([])
      setFotoZorunlu(false)
      setAciklamaZorunlu(false)
    }
  }, [acik, gorev])

  if (!acik) return null

  const aktifSubeler = subeler.filter((s) => s.aktif !== false)
  const aktifPozisyonlar = pozisyonlar.filter((p) => p.aktif !== false)
  const aktifPersoneller = personeller.filter((p) => p.aktif !== false)

  function toggle(dizi: string[], deger: string, setter: (v: string[]) => void) {
    setter(dizi.includes(deger) ? dizi.filter((x) => x !== deger) : [...dizi, deger])
  }

  async function kaydet() {
    if (!firebaseUser) return
    setHata(null)
    setKaydediliyor(true)
    try {
      const hedef: GorevHedefi = {
        subeIdleri: seciliSubeler,
        pozisyonIdleri: seciliPozisyonlar,
        personelIdleri: seciliPersoneller,
      }
      const ortak: Omit<GorevOlusturData, 'atayanId' | 'atayanRol'> = {
        baslik,
        aciklama,
        tipi,
        hedef,
        baslangicTarihi,
        bitisTarihi: bitisTarihi || undefined,
        zamanAraligi: zamanVar && zBas && zBit ? { baslangic: zBas, bitis: zBit } : undefined,
        mesaiDisiDahil,
        paylasimli,
        fotoZorunlu,
        aciklamaZorunlu,
        yapanIsim: kullanici?.isim,
      }

      if (gorev) {
        await gorevGuncelle(gorev.id, ortak, firebaseUser.uid, kullanici?.isim, 'boss')
      } else {
        await gorevOlustur({
          ...ortak,
          atayanId: firebaseUser.uid,
          atayanRol: 'boss',
        })
      }
      onKaydedildi()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  const ipucu = tarihIpucu(tipi, baslangicTarihi)

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-brand-600" />
            <h2 className="font-bold text-gray-900">
              {duzenleme ? 'Görevi Düzenle' : 'Yeni Görev'}
            </h2>
          </div>
          <button onClick={onKapat} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {hata && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
              {hata}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Başlık</label>
            <input
              type="text"
              value={baslik}
              onChange={(e) => setBaslik(e.target.value)}
              placeholder="Örn. Sebze reyonunu yenile"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Açıklama</label>
            <textarea
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
              rows={3}
              placeholder="Görev detayları"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Tip</label>
            <select
              value={tipi}
              onChange={(e) => setTipi(e.target.value as GorevTipi)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              {TIP_SECENEKLERI.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {tipi === 'tekSeferlik' ? 'Yapılacağı tarih' : 'Başlangıç tarihi'}
              </label>
              <input
                type="date"
                value={baslangicTarihi}
                onChange={(e) => setBaslangicTarihi(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            {tipi !== 'tekSeferlik' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Bitiş tarihi (ops.)
                </label>
                <input
                  type="date"
                  value={bitisTarihi}
                  onChange={(e) => setBitisTarihi(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            )}
          </div>
          {ipucu && (
            <p className="text-xs text-brand-700 bg-brand-50 rounded-lg p-2">{ipucu}</p>
          )}

          {/* Saat aralığı */}
          <div className="border border-gray-200 rounded-xl p-3 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={zamanVar}
                onChange={(e) => setZamanVar(e.target.checked)}
                className="rounded"
              />
              <span>Belirli saat aralığında yapılsın</span>
            </label>
            {zamanVar && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Başlangıç</label>
                    <input
                      type="time"
                      value={zBas}
                      onChange={(e) => setZBas(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Bitiş</label>
                    <input
                      type="time"
                      value={zBit}
                      onChange={(e) => setZBit(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                </div>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mesaiDisiDahil}
                    onChange={(e) => setMesaiDisiDahil(e.target.checked)}
                    className="rounded mt-0.5"
                  />
                  <span>
                    Mesaide olmayanlara da göster
                    <span className="block text-xs text-gray-500">
                      Kapalıysa: sadece bu saatlerde vardiyası olanlar görür. Açıksa: ilgili herkes görür.
                    </span>
                  </span>
                </label>
              </>
            )}
          </div>

          {/* Hedef */}
          <div className="border border-gray-200 rounded-xl p-3 space-y-3">
            <p className="text-sm font-semibold text-gray-700">Kime atanacak?</p>

            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Şubeler (şubedeki herkes)</p>
              <div className="flex flex-wrap gap-1.5">
                {aktifSubeler.map((s) => (
                  <Chip key={s.id} aktif={seciliSubeler.includes(s.id)}
                    onClick={() => toggle(seciliSubeler, s.id, setSeciliSubeler)}>
                    {s.isim}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Pozisyonlar</p>
              <div className="flex flex-wrap gap-1.5">
                {aktifPozisyonlar.map((p) => (
                  <Chip key={p.id} aktif={seciliPozisyonlar.includes(p.id)}
                    onClick={() => toggle(seciliPozisyonlar, p.id, setSeciliPozisyonlar)}>
                    {p.isim}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Kişiler</p>
              <div className="flex flex-wrap gap-1.5">
                {aktifPersoneller.map((p) => (
                  <Chip key={p.id} aktif={seciliPersoneller.includes(p.id)}
                    onClick={() => toggle(seciliPersoneller, p.id, setSeciliPersoneller)}>
                    {p.isim}
                  </Chip>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-400">En az bir şube, pozisyon veya kişi seçilmeli.</p>
          </div>

          {/* Paylaşımlı (vardiya) görev */}
          <label className="flex items-start gap-2 text-sm cursor-pointer border border-amber-200 bg-amber-50 rounded-xl p-3">
            <input
              type="checkbox"
              checked={paylasimli}
              onChange={(e) => setPaylasimli(e.target.checked)}
              className="rounded mt-0.5"
            />
            <span>
              Vardiyada biri yapsın yeter (paylaşımlı görev)
              <span className="block text-xs text-gray-500 mt-0.5">
                Açıksa: hedeftekilerden <b>biri</b> yapınca görev biter, diğerlerinden istenmez. Kimse yapmazsa tek bir "yapılmadı" uyarısı yöneticiye gider. İzinli kişi suçlanmaz; yerine bakan yapabilir. (Saat aralığı + şube/pozisyon ile birlikte kullan.)
              </span>
            </span>
          </label>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={aciklamaZorunlu}
                onChange={(e) => setAciklamaZorunlu(e.target.checked)} className="rounded" />
              <span>Tamamlarken açıklama zorunlu</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={fotoZorunlu}
                onChange={(e) => setFotoZorunlu(e.target.checked)} className="rounded" />
              <span>Tamamlarken fotoğraf zorunlu</span>
            </label>
          </div>
        </div>

        <div className="flex gap-2 px-5 py-4 border-t border-gray-200 sticky bottom-0 bg-white rounded-b-2xl">
          <button onClick={onKapat} disabled={kaydediliyor}
            className="flex-1 border border-gray-300 text-gray-700 rounded-lg py-2 text-sm font-semibold hover:bg-gray-50">
            İptal
          </button>
          <button onClick={kaydet} disabled={kaydediliyor}
            className="btn-primary flex-1 py-2 text-sm disabled:opacity-50">
            {kaydediliyor ? 'Kaydediliyor…' : duzenleme ? 'Güncelle' : 'Oluştur'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Chip({
  aktif, onClick, children,
}: { aktif: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
        aktif
          ? 'bg-brand-100 text-brand-700 border-brand-300'
          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
      }`}>
      {children}
    </button>
  )
}
